// Hypothesis Intake — the first 2 minutes (one-question-first, ADAPTIVE).
//
// Conversation, not a wizard: one primary question, then only the follow-ups the
// idea hasn't already answered (skip cause/edge when the first answer contains
// them), one at a time. Rejected-lane recognition runs after the idea (conservative:
// only High/Medium surface) and is styled as research MEMORY, not a warning. When
// nothing matches, a positive "fresh angle" note keeps momentum. The final screen
// is the BEGINNER view (why / what-would-prove-it-wrong / Build) with an opt-in
// Advanced expander. No AI, no network.
// See docs/track-record/HYPOTHESIS-INTAKE-UX-SPEC.md.

import { useMemo, useState } from 'react';
import { X, Compass, ChevronDown, ChevronRight, Sparkles } from 'lucide-react';
import type { HypothesisCard, RejectedLaneMatch } from '../../types/validation';
import { matchRejectedLane, shouldSurface, failureAxisLabel } from '../../engine/validation/rejectedLanes';
import {
  buildDraftCard,
  evaluateReadiness,
  extractSignals,
  mechanismFamilyLabel,
} from '../../engine/validation/hypothesisCard';

export interface HypothesisIntakeModalProps {
  onComplete: (card: HypothesisCard) => void;
  onClose: () => void;
  onAuditEvent?: (type: 'lane_dismissed' | 'lane_overridden', payload: Record<string, unknown>) => void;
  // Optional seed for the first question. Production callers omit it (starts
  // empty); the Validation Playground passes a fixture idea to demo a scenario.
  initialIdea?: string;
}

type Step = 'idea' | 'lane' | 'cause' | 'edge' | 'ready';

function newId(): string {
  try { return crypto.randomUUID(); } catch { return 'hyp_' + Math.random().toString(36).slice(2); }
}

export default function HypothesisIntakeModal({ onComplete, onClose, onAuditEvent, initialIdea }: HypothesisIntakeModalProps) {
  const [step, setStep] = useState<Step>('idea');
  const [ideaText, setIdeaText] = useState(initialIdea ?? '');
  const [cause, setCause] = useState('');
  const [edge, setEdge] = useState('');
  const [difference, setDifference] = useState('');
  const [lane, setLane] = useState<RejectedLaneMatch | null>(null);
  const [freshLane, setFreshLane] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [meta] = useState(() => ({ id: newId(), createdAt: new Date().toISOString() }));

  const card = useMemo<HypothesisCard>(
    () => buildDraftCard({ id: meta.id, ideaText, cause, edge, createdAt: meta.createdAt }),
    [meta, ideaText, cause, edge],
  );
  const readiness = useMemo(() => evaluateReadiness(card), [card]);

  // The next screen needed, given what's already filled. Adaptiveness falls out
  // of "only ask for a field that's still empty".
  const stepFor = (c: string, e: string): Step => (!c.trim() ? 'cause' : !e.trim() ? 'edge' : 'ready');

  // Step 1 → prefill anything the idea already answered, recognise a rejected
  // lane (conservative), then go to the first follow-up still needed.
  function submitIdea() {
    const sig = extractSignals(ideaText);
    const nextCause = cause.trim() ? cause : sig.causeText;
    const nextEdge = edge.trim() ? edge : sig.edgeText;
    if (nextCause !== cause) setCause(nextCause);
    if (nextEdge !== edge) setEdge(nextEdge);

    const m = matchRejectedLane(ideaText);
    if (shouldSurface(m)) {
      setLane(m); setFreshLane(false); setStep('lane');
    } else {
      setLane(null); setFreshLane(true);
      setStep(stepFor(nextCause, nextEdge));
    }
  }

  const afterLane = () => setStep(stepFor(cause, edge));
  function dismissLane() {
    onAuditEvent?.('lane_dismissed', { hypothesisId: meta.id, laneId: lane?.laneId });
    setLane(null); afterLane();
  }
  function overrideLane() {
    onAuditEvent?.('lane_overridden', { hypothesisId: meta.id, laneId: lane?.laneId, difference });
    setLane(null); afterLane();
  }
  function proceedPastLane() {
    onAuditEvent?.('lane_overridden', { hypothesisId: meta.id, laneId: lane?.laneId, action: 'test_anyway' });
    setLane(null); afterLane();
  }
  function applyBreak(suggestion: string) {
    onAuditEvent?.('lane_overridden', { hypothesisId: meta.id, laneId: lane?.laneId, action: 'break_axis', suggestion });
    setCause(suggestion); setLane(null);
    setStep(!edge.trim() ? 'edge' : 'ready');
  }

  const freshNote = freshLane && step !== 'idea' && step !== 'lane' ? (
    <div className="flex items-center gap-1.5 text-xs text-emerald-700 bg-emerald-50 border border-emerald-100 rounded px-2 py-1.5">
      <Sparkles className="w-3.5 h-3.5 shrink-0" />
      Fresh angle — this doesn&apos;t closely match any idea we&apos;ve already seen fail.
    </div>
  ) : null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-medium text-gray-800">Sharpen your idea</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4">
          {/* ── Step 1: one question, nothing else ── */}
          {step === 'idea' && (
            <div className="space-y-3">
              <label className="block text-base font-semibold text-gray-900">
                What do you think will cause this trade to work?
              </label>
              <p className="text-xs text-gray-500">
                Plain language is fine — the idea and why you think it works. If you mention the size, we won&apos;t
                ask again.
              </p>
              <textarea
                autoFocus
                value={ideaText}
                onChange={(e) => setIdeaText(e.target.value)}
                rows={3}
                placeholder="e.g. When BTC funding goes deeply negative, longs get paid ~0.3% a week to hold…"
                className="w-full border border-gray-300 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-fuchsia-400"
              />
              <div className="flex justify-end">
                <button
                  onClick={submitIdea}
                  disabled={!ideaText.trim()}
                  className="px-4 py-1.5 bg-fuchsia-600 hover:bg-fuchsia-700 disabled:opacity-40 text-white text-sm font-medium rounded"
                >
                  Continue
                </button>
              </div>
            </div>
          )}

          {/* ── Rejected-lane recognition: research MEMORY, neutral styling ── */}
          {step === 'lane' && lane && (
            <div className="space-y-3">
              <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
                <div className="flex items-center gap-2 text-gray-700 font-medium text-sm">
                  <Compass className="w-4 h-4 text-indigo-500" /> Here&apos;s what previous researchers already learned
                </div>
                <p className="text-sm text-gray-700 mt-2">
                  Your idea <span className="font-medium">resembles</span> the &ldquo;{lane.laneName}&rdquo; lane.
                  It broke on one axis — <span className="font-medium">{failureAxisLabel(lane.failureAxis)}</span>:
                  {' '}{lane.memory}.
                </p>
                <div className="mt-2">
                  <div className="text-xs font-medium text-gray-600 mb-1">What makes your idea different?</div>
                  <input
                    value={difference}
                    onChange={(e) => setDifference(e.target.value)}
                    placeholder="(tell us and we'll treat it as a fresh lane)"
                    className="w-full border border-gray-300 rounded p-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-400"
                  />
                </div>
                <div className="mt-2">
                  <div className="text-xs font-medium text-gray-600 mb-1">Or break the failure axis:</div>
                  <div className="flex flex-wrap gap-1.5">
                    {lane.breakSuggestions.map((s) => (
                      <button
                        key={s}
                        onClick={() => applyBreak(s)}
                        className="px-2 py-1 text-xs rounded border border-gray-300 bg-white hover:bg-gray-100 text-gray-700"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap justify-end gap-2">
                <button onClick={dismissLane} className="px-3 py-1.5 text-sm rounded border hover:bg-gray-50">
                  Not the same — dismiss
                </button>
                {difference.trim() ? (
                  <button onClick={overrideLane} className="px-3 py-1.5 text-sm rounded bg-fuchsia-600 hover:bg-fuchsia-700 text-white">
                    That&apos;s different — continue
                  </button>
                ) : (
                  <button onClick={proceedPastLane} className="px-3 py-1.5 text-sm rounded bg-gray-700 hover:bg-gray-800 text-white">
                    Test it anyway
                  </button>
                )}
              </div>
            </div>
          )}

          {/* ── Follow-up: the mechanism (only if not already given) ── */}
          {step === 'cause' && (
            <div className="space-y-3">
              {freshNote}
              <label className="block text-base font-semibold text-gray-900">
                Why would the market pay you for it?
              </label>
              <p className="text-xs text-gray-500">One line on the mechanism — who&apos;s on the other side, and why it pays.</p>
              <input
                autoFocus
                value={cause}
                onChange={(e) => setCause(e.target.value)}
                placeholder="e.g. shorts pay longs to absorb leverage demand"
                className="w-full border border-gray-300 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-fuchsia-400"
              />
              <div className="flex justify-between">
                <button onClick={() => setStep('idea')} className="px-3 py-1.5 text-sm rounded border hover:bg-gray-50">Back</button>
                <button onClick={() => setStep(!edge.trim() ? 'edge' : 'ready')} className="px-4 py-1.5 bg-fuchsia-600 hover:bg-fuchsia-700 text-white text-sm font-medium rounded">
                  Continue
                </button>
              </div>
            </div>
          )}

          {/* ── Follow-up: the edge (only if not already given) ── */}
          {step === 'edge' && (
            <div className="space-y-3">
              {freshNote}
              <label className="block text-base font-semibold text-gray-900">Which way, and roughly how big?</label>
              <p className="text-xs text-gray-500">A few bps a trade, or a few percent a month? A rough size is fine.</p>
              <input
                autoFocus
                value={edge}
                onChange={(e) => setEdge(e.target.value)}
                placeholder="e.g. long; ~0.3% per week before costs"
                className="w-full border border-gray-300 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-fuchsia-400"
              />
              <div className="flex justify-between">
                <button onClick={() => setStep('cause')} className="px-3 py-1.5 text-sm rounded border hover:bg-gray-50">Back</button>
                <button onClick={() => setStep('ready')} className="px-4 py-1.5 bg-fuchsia-600 hover:bg-fuchsia-700 text-white text-sm font-medium rounded">
                  Continue
                </button>
              </div>
            </div>
          )}

          {/* ── Beginner view (why / what-would-prove-it-wrong / Build) + Advanced ── */}
          {step === 'ready' && (
            <div className="space-y-3">
              {freshNote}
              <div className="rounded-md border border-gray-200 p-3 space-y-2">
                <div>
                  <div className="text-xs font-medium text-gray-500">Why it might work</div>
                  <div className="text-sm text-gray-900">{card.mechanism || <span className="text-gray-400">—</span>}</div>
                </div>
                <div>
                  <div className="text-xs font-medium text-gray-500">What would prove it wrong</div>
                  <div className="text-sm text-gray-900">{card.falsification}</div>
                </div>
              </div>

              <button
                onClick={() => setShowAdvanced((v) => !v)}
                className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
              >
                {showAdvanced ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                {showAdvanced ? 'Hide details' : 'Show details'}
              </button>

              {showAdvanced && (
                <div className="rounded-md border border-gray-200 bg-gray-50 p-3 text-xs text-gray-700 space-y-1.5">
                  <Detail label="Hypothesis" value={card.hypothesisSentence} />
                  <Detail label="Mechanism family" value={mechanismFamilyLabel(card.mechanismFamily)} />
                  <Detail label="Expected edge" value={card.expectedEdge} />
                  <Detail label="Mechanism" value={card.mechanism} />
                  <Detail label="Required data" value={card.requiredData || '(to be confirmed)'} />
                  <Detail label="Falsifies if" value={card.falsification} />
                  <Detail label="Validation plan" value={card.validationPlan} />
                </div>
              )}

              {!readiness.ready && (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
                  <p className="text-sm text-amber-900">
                    This idea needs one more assumption before we can test it: <strong>{readiness.missing[0]}</strong>.
                  </p>
                  {readiness.missing[0]?.includes('mechanism') && (
                    <input
                      value={cause}
                      onChange={(e) => setCause(e.target.value)}
                      placeholder="Add the why in one line"
                      className="mt-2 w-full border border-amber-300 rounded p-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-amber-400"
                    />
                  )}
                  {readiness.missing[0]?.includes('big') && (
                    <input
                      value={edge}
                      onChange={(e) => setEdge(e.target.value)}
                      placeholder="Direction + rough size"
                      className="mt-2 w-full border border-amber-300 rounded p-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-amber-400"
                    />
                  )}
                </div>
              )}

              <div className="flex justify-between">
                <button onClick={() => setStep('edge')} className="px-3 py-1.5 text-sm rounded border hover:bg-gray-50">Back</button>
                <button
                  onClick={() => onComplete(card)}
                  disabled={!readiness.ready}
                  className="px-4 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white text-sm font-medium rounded"
                >
                  Build this
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-2">
      <span className="font-medium text-gray-500 shrink-0">{label}:</span>
      <span className="text-gray-800">{value || '—'}</span>
    </div>
  );
}
